<template>
  <div class="viz-root relative w-full">
    <div v-if="!items || items.length === 0" class="text-sm text-gray-400 py-8 text-center">
      No data available
    </div>
    <svg
      v-else
      :viewBox="`0 0 ${W} ${height}`"
      width="100%"
      :height="height"
      role="img"
      :aria-label="ariaLabel"
      @mouseleave="hovered = -1"
    >
      <!-- baseline -->
      <line :x1="gutter" :y1="0" :x2="gutter" :y2="height" stroke="#c3c2b7" stroke-width="1" />
      <g v-for="(row, i) in rows" :key="row.label">
        <!-- hover band -->
        <rect
          :x="0" :y="row.y - (rowH - barH) / 2" :width="W" :height="rowH"
          fill="#0b0b0b" :opacity="hovered === i ? 0.04 : 0"
          @mousemove="onMove($event, i)" @mouseenter="hovered = i"
        />
        <!-- category label -->
        <text
          :x="gutter - 8" :y="row.y + barH * 0.72" text-anchor="end"
          font-size="12" fill="#52514e"
          style="font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;"
        >{{ truncate(row.label) }}</text>
        <!-- bar (square at baseline, rounded data-end) -->
        <path :d="barPath(row)" :fill="row.color" :opacity="hovered === -1 || hovered === i ? 1 : 0.55" />
        <!-- value label -->
        <text
          :x="Math.max(row.x1 + 6, gutter + 6)" :y="row.y + barH * 0.72"
          font-size="12" fill="#0b0b0b"
          style="font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; font-variant-numeric: tabular-nums;"
        >{{ format(row.value) }}</text>
      </g>
    </svg>

    <!-- tooltip -->
    <div
      v-if="hovered >= 0"
      class="pointer-events-none absolute z-10 rounded-md bg-[#0b0b0b] text-white text-xs px-2 py-1 shadow-lg"
      :style="{ left: tip.x + 'px', top: tip.y + 'px', transform: 'translate(-50%, -120%)' }"
    >
      <div class="font-medium">{{ rows[hovered].label }}</div>
      <div class="tabular-nums">{{ format(rows[hovered].value) }}</div>
      <div v-if="rows[hovered].sub" class="text-gray-300">{{ rows[hovered].sub }}</div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

// Single-hue horizontal bar chart (magnitude comparison across labelled rows).
// One series → identity comes from the row labels, so no legend/color key.
export default {
  props: {
    items: { type: Array, default: () => [] }, // [{ label, value, color?, sub? }]
    color: { type: String, default: "#2a78d6" }, // series-1 (light)
    format: { type: Function, default: (v) => v.toLocaleString() },
    ariaLabel: { type: String, default: "Bar chart" },
    gutter: { type: Number, default: 150 },
  },
  setup(props) {
    const W = 620;
    const rowH = 30;
    const barH = 16;
    const radius = 4;
    const hovered = ref(-1);
    const tip = ref({ x: 0, y: 0 });

    const height = computed(() => Math.max(props.items.length * rowH + 8, 40));
    const maxVal = computed(() => Math.max(1, ...props.items.map((d) => d.value || 0)));

    const rows = computed(() =>
      props.items.map((d, i) => {
        const y = i * rowH + 4;
        const barW = ((d.value || 0) / maxVal.value) * (W - props.gutter - 90);
        return {
          label: d.label,
          value: d.value || 0,
          sub: d.sub,
          color: d.color || props.color,
          y,
          x1: props.gutter + barW,
          barW,
        };
      })
    );

    const barPath = (row) => {
      const x0 = props.gutter;
      const x1 = Math.max(row.x1, x0 + 0.5);
      const y = row.y;
      const r = Math.min(radius, row.barW);
      if (row.barW <= r) {
        return `M${x0},${y} H${x1} V${y + barH} H${x0} Z`;
      }
      return `M${x0},${y} H${x1 - r} Q${x1},${y} ${x1},${y + r} V${y + barH - r} Q${x1},${y + barH} ${x1 - r},${y + barH} H${x0} Z`;
    };

    const onMove = (e, i) => {
      hovered.value = i;
      const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
      tip.value = { x: e.clientX - rect.left, y: rows.value[i].y / height.value * rect.height };
    };

    const truncate = (s) => {
      const str = (s || "").toString();
      return str.length > 22 ? str.slice(0, 21) + "…" : str;
    };

    return { W, rowH, barH, height, rows, barPath, hovered, tip, onMove, truncate };
  },
};
</script>
